import { pick } from 'lodash';
import React from 'react';
import { ThemeProvider } from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { Users, MapMarkerAlt, HourglassHalf, PowerOff } from 'styled-icons/fa-solid';
import { AccessTime } from 'styled-icons/material';

import { useStore } from '@src/models';
import { PublicLayout } from '@src/layouts';
import { RenderAsHTMLStyles, GoogleMap, Tooltip } from '@src/features/common';
import { makeDeadline, makeStartDate } from '@src/features/dashboard';
import { getJobId, JobDescriptionLabels, JobFieldSet, OnboardingModal } from '@src/features/job';
import { Loader } from 'src/components';
import { LayoutBox, BaseBox, Flex, Card, Text, TextBox, Circle, Button, BgImage, Link, Delimiter } from 'src/kit';
import { employerTheme } from 'src/theme';
import { JOB_STATES_MAP, ROUTES } from 'src/constants';
import { SimilarJobContainer } from './SimilarJob.styled';
import { ClosedJob } from './ClosedJob';

const USER_ROLES_DISABLED_APPLY = {
  employer: 'employer',
  university: 'university',
  freelancer: 'freelancer'
};

const JobDetails = observer(function Job() {
  const { routerStore, authStore, jobsStore, userStore } = useStore();
  const [showOnboardingModal, setShowOnboardingModal] = React.useState(false);
  const { jobId } = useParams<{ jobId: string }>();
  const job = jobsStore.getJobById(jobId);

  const goToJobApplicationForm = () => {
    routerStore.push({
      pathname: ROUTES.jobs.applications.new.replace(':jobId', String(getJobId(job))),
      search: routerStore.location.search
    });
  };

  const handleApply = React.useCallback(() => {
    if (job.skipExternalUrl) {
      if (!authStore.isAuthorized) {
        setShowOnboardingModal(true);
      } else {
        goToJobApplicationForm();
      }
    } else {
      if (!authStore.isAuthorized) {
        goToJobApplicationForm();
      } else {
        jobsStore.simpleApplyForJob(job);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  if (jobsStore.isFetching) {
    return <Loader css={{ height: '200px' }} />;
  }

  const office = job.office
    ? pick(job.office, ['lat', 'lon', 'address', 'city'])
    : pick(job.company, ['lat', 'lon', 'address', 'city']);

  const disableApplyButton =
    // if your role doesnt assume u can apply or you already applied or job status is closed or unpublished etc.
    Boolean(Object.values(USER_ROLES_DISABLED_APPLY).find(role => userStore?.user?.role === role)) ||
    job.is_applied ||
    job.state !== JOB_STATES_MAP.published;

  const applyButtonTooltipText = {
    default: `You already applied to this position or your role doesn't allow it`,
    unpublished: 'Job is not published'
  }[job.state !== JOB_STATES_MAP.published ? 'unpublished' : 'default'];

  const isJobClosed = job.state === JOB_STATES_MAP.closed;

  return (
    <ThemeProvider theme={employerTheme}>
      <PublicLayout p={0} maxWidth="initial">
        <BaseBox color="textSecondary">
          {showOnboardingModal && (
            <OnboardingModal
              job={job}
              onClose={() => setShowOnboardingModal(false)}
              onSubmit={goToJobApplicationForm}
            />
          )}
          <LayoutBox position="relative" height={380}>
            <BgImage src={job.company.cover_picture_url} fullParentSize filter="brightness(0.8)" />
            <Flex position="relative" py={52} flexDirection="column" alignItems="center" justifyContent="center">
              <Circle as="img" src={job.company.logo_url} size={160} />
              <Text mt={25} color="white" fontSize={18} fontWeight="light" fontStyle="italic" textAlign="center">
                {job.company.motto}
              </Text>
            </Flex>
          </LayoutBox>
          {!isJobClosed ? (
            <Card position="relative" m="-90px auto 120px" p="30px 40px" maxWidth={840} bg="white" boxShadow="shadow1">
              <Text mb={20} color={job.company.color} fontSize={20} fontWeight="bold" textStyle="ellipsis">
                {job.position_title}
              </Text>
              <Flex>
                <Card mr={15} flex="1 1 0%" borderRight="1px solid" borderColor="paleGreyTwo">
                  <JobDescriptionLabels job={job} iconColor={job.company.color} />
                </Card>
                <Flex flexDirection="column" justifyContent="center">
                  <JobFieldSet icon={HourglassHalf} iconColor={job.company.color}>
                    {makeDeadline(job.deadline, 'd MMM yyyy')}
                  </JobFieldSet>
                  {(job.start_date || job.length) && (
                    <JobFieldSet icon={PowerOff} iconColor={job.company.color}>
                      {makeStartDate({ startDate: job.start_date, length: job.length })}
                    </JobFieldSet>
                  )}
                </Flex>
              </Flex>
              <Delimiter my={30} />
              <Flex>
                <BaseBox mr={20} flex="1 1 0%" color="#333333">
                  {job.is_anonymous && !job.candidate_is_trusted && (
                    <div>
                      <BaseBox mb={50}>
                        <Field label="Company size:" value={`${job.company.size} employers`} />
                        <Field label="Company industry:" value={job.company.industry} />
                        <Field label="Type of role:" value={job.sub_themes.map(v => v.name).join(', ')} />
                        <Field label="Main focus:" value={job.focuses.map(v => v.name).join(', ')} />
                      </BaseBox>
                      <TextBox mb={20} color="textPrimary" fontSize={16} fontWeight="bold" textStyle="firstLetter">
                        Profile of the ideal candidate
                      </TextBox>
                      <Field label="Experience:" value={job.experiences.join(', ')} />
                      <Field label="Main focus:" value={job.ideal_profile_sub_themes.map(v => v.name).join(', ')} />
                      <Field label="Specialties:" value={job.ideal_profile_focuses.map(v => v.name).join(', ')} />
                    </div>
                  )}
                  {job.brief && (
                    <RenderAsHTMLStyles dangerouslySetInnerHTML={{ __html: job.brief }}></RenderAsHTMLStyles>
                  )}
                </BaseBox>
                <LayoutBox flex="1 1 0%" maxWidth={320}>
                  {<GoogleMap address={office?.address} />}
                  {office?.address && (
                    <>
                      <Text mb={20} color={job.company.color} fontSize={16} fontWeight="bold" textStyle="caps">
                        Office
                      </Text>
                      <JobFieldSet icon={MapMarkerAlt}>{office.address}</JobFieldSet>
                    </>
                  )}
                  {Boolean(job.similar_jobs?.length) && (
                    <BaseBox mt={30}>
                      <Text color={job.company.color} fontSize={16} textStyle="caps">
                        More jobs
                      </Text>
                      {job.similar_jobs.map(similarJob => (
                        <SimilarJobContainer key={similarJob.id}>
                          <Link
                            to={ROUTES.jobs.job.replace(':jobId', similarJob.id)}
                            openInNewTab={true}
                            mb={5}
                            color={job.company.color}
                          >
                            {similarJob.position_title}
                          </Link>
                          <JobFieldSet icon={Users}>{similarJob.team?.name}</JobFieldSet>
                          <JobFieldSet icon={MapMarkerAlt}>
                            {similarJob.office?.city || similarJob.company?.city}
                          </JobFieldSet>
                          <JobFieldSet icon={AccessTime}>{similarJob.employment_type}</JobFieldSet>
                        </SimilarJobContainer>
                      ))}
                    </BaseBox>
                  )}
                </LayoutBox>
              </Flex>
              <Delimiter my={30} />
              <Flex justifyContent="flex-end">
                {job.is_external ? (
                  <>
                    {job.external_link && (
                      <Button
                        as="a"
                        href={job.external_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="primary"
                        size="md"
                        width="220px"
                      >
                        Apply on External Site
                      </Button>
                    )}
                    {!job.external_link && job.external_contact_info && (
                      <Card p={20} bg="paleGrey">
                        <Text fontWeight="bold" mb={15}>Contact Us</Text>
                        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{job.external_contact_info}</pre>
                      </Card>
                    )}
                    {!job.external_link && !job.external_contact_info && (
                      <Text color="error" fontWeight="bold">
                        No external application method provided.
                      </Text>
                    )}
                  </>
                ) : (
                  <Tooltip disabled={!disableApplyButton} title={disableApplyButton ? applyButtonTooltipText : ''}>
                    <Button onClick={handleApply} disabled={disableApplyButton} variant="primary" size="md" width="120px">
                      Apply
                    </Button>
                  </Tooltip>
                )}
              </Flex>
            </Card>
          ) : (
            <ClosedJob slug={job.company.slug || ''} />
          )}
        </BaseBox>
      </PublicLayout>
    </ThemeProvider>
  );
});

export default JobDetails;

const Field = ({ label, value, textStyle, ...rest }: any) =>
  value && (
    <Flex mb={20} {...rest}>
      <Label>{label}</Label>
      <Value textStyle={textStyle}>{value}</Value>
    </Flex>
  );

const Label = props => <TextBox mr={20} color="textPrimary" fontSize={16} {...{ minWidth: '140px' }} {...props} />;
const Value = props => (
  <TextBox color="textSecondary" fontWeight="normal" fontSize={16} textStyle="capitalize" {...props} />
);
